import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    ColumnDef,
    SortingState,
    flexRender,
} from "@tanstack/react-table";
import { Payment } from "../api/users";
import { format } from "date-fns";
import { ru } from "date-fns/locale";

interface PaymentsTableProps {
    data: Payment[];
    isLoading: boolean;
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
}

export const PaymentsTable: React.FC<PaymentsTableProps> = ({
    data,
    isLoading,
    pagination,
    onPageChange,
    onPageSizeChange,
}) => {
    const { t } = useTranslation("payments");
    const [sorting, setSorting] = useState<SortingState>([]);

    const formatDate = (dateString: string) => {
        return format(new Date(dateString), "dd.MM.yyyy HH:mm", { locale: ru });
    };

    const formatAmount = (amount: number) => {
        return new Intl.NumberFormat("ru-RU", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    const getCurrencyBadgeColor = (currency: number) => {
        return currency === 1
            ? "bg-green-100 text-green-800"
            : "bg-blue-100 text-blue-800";
    };

    const getType2BadgeColor = (type2: number | null) => {
        switch (type2) {
            case 0:
                return "bg-red-100 text-red-800"; // расход
            case 1:
                return "bg-green-100 text-green-800"; // приход
            case 2:
                return "bg-gray-100 text-gray-800"; // нейтрально
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    const getType2Text = (type2: number | null) => {
        switch (type2) {
            case 0:
                return t("type2.expense");
            case 1:
                return t("type2.income");
            case 2:
                return t("type2.neutral");
            default:
                return t("type2.unknown");
        }
    };

    const getCurrencyText = (currency: number) => {
        switch (currency) {
            case 0:
                return t("currency.usd");
            case 1:
                return t("currency.crypto");
            case 100:
                return t("currency.none");
            default:
                return t("currency.unknown");
        }
    };

    const getAnualText = (anual: number | null) => {
        switch (anual) {
            case 1:
                return t("anual.structure");
            case 2:
                return t("anual.welfare");
            case 3:
                return t("anual.tourism");
            case 4:
                return t("anual.star");
            default:
                return t("anual.none");
        }
    };

    const columns: ColumnDef<Payment>[] = [
            {
                accessorKey: "id",
                header: t("table.id"),
                size: 80,
                cell: ({ row }) => (
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {row.original.id}
                    </div>
                ),
            },
            {
                accessorKey: "user",
                header: t("table.user"),
                size: 250,
                cell: ({ row }) => (
                    <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {row.original.user.name} (ID: {row.original.user.id}
                            )
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                            {row.original.user.email}
                        </div>
                        {row.original.user.telegram_username && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                @{row.original.user.telegram_username}
                            </div>
                        )}
                    </div>
                ),
            },
            {
                accessorKey: "sum",
                header: t("table.amount"),
                size: 120,
                cell: ({ row }) => (
                    <div className="text-sm text-gray-900 dark:text-white">
                        {formatAmount(row.original.sum)}
                    </div>
                ),
            },
            {
                accessorKey: "total",
                header: t("table.total"),
                size: 120,
                cell: ({ row }) => (
                    <div className="text-sm text-gray-900 dark:text-white">
                        {formatAmount(row.original.total)}
                    </div>
                ),
            },
            {
                accessorKey: "currency",
                header: t("table.currency"),
                size: 100,
                cell: ({ row }) => (
                    <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCurrencyBadgeColor(
                            row.original.currency
                        )}`}
                    >
                        {getCurrencyText(row.original.currency)}
                    </span>
                ),
            },
            {
                accessorKey: "type2",
                header: t("table.type2"),
                size: 120,
                cell: ({ row }) => (
                    <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getType2BadgeColor(
                            row.original.type2
                        )}`}
                    >
                        {getType2Text(row.original.type2)}
                    </span>
                ),
            },
            {
                accessorKey: "time",
                header: t("table.date"),
                size: 150,
                cell: ({ row }) => (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(row.original.time)}
                    </div>
                ),
            },
            {
                accessorKey: "desc",
                header: t("table.description"),
                size: 200,
                cell: ({ row }) => (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        {row.original.desc}
                    </div>
                ),
            },
            {
                accessorKey: "additional_desc",
                header: t("table.additional_desc"),
                size: 200,
                cell: ({ row }) => (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        {row.original.additional_desc || "-"}
                    </div>
                ),
            },
            {
                accessorKey: "anual",
                header: t("table.anual"),
                size: 120,
                cell: ({ row }) => (
                    <div className="text-sm text-gray-900 dark:text-white">
                        {getAnualText(row.original.anual)}
                    </div>
                ),
            },
        ];

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        manualPagination: true, // Отключаем клиентскую пагинацию
    });

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col">
            {/* Таблица с фиксированным заголовком */}
            <div className="flex-1 overflow-auto">
                <table className="min-w-full table-fixed">
                    <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0 z-10">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th
                                        key={header.id}
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50 dark:bg-gray-900"
                                        style={{
                                            width: `${header.getSize()}px`,
                                        }}
                                        onClick={header.column.getToggleSortingHandler()}
                                    >
                                        <div className="flex items-center space-x-1">
                                            <span>
                                                {flexRender(
                                                    header.column.columnDef
                                                        .header,
                                                    header.getContext()
                                                )}
                                            </span>
                                            {{
                                                asc: " 🔼",
                                                desc: " 🔽",
                                            }[
                                                header.column.getIsSorted() as string
                                            ] ?? null}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
                        {table.getRowModel().rows.map((row) => (
                            <tr
                                key={row.id}
                                className="hover:bg-gray-50 dark:bg-gray-900"
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <td
                                        key={cell.id}
                                        className="px-6 py-4 whitespace-nowrap"
                                        style={{
                                            width: `${cell.column.getSize()}px`,
                                        }}
                                    >
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext()
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Пагинация */}
            {pagination.last_page > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                            {t("pagination.showing")}{" "}
                            {pagination.total > 0
                                ? (pagination.current_page - 1) *
                                      pagination.per_page +
                                  1
                                : 0}{" "}
                            -{" "}
                            {Math.min(
                                pagination.current_page * pagination.per_page,
                                pagination.total
                            )}{" "}
                            {t("pagination.to")} {pagination.total}{" "}
                            {t("pagination.results")}
                        </div>
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                                <label className="text-sm text-gray-700 dark:text-gray-300">
                                    {t("pagination.per_page")}:
                                </label>
                                <select
                                    value={pagination.per_page}
                                    onChange={(e) =>
                                        onPageSizeChange(Number(e.target.value))
                                    }
                                    className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                                >
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() =>
                                        onPageChange(
                                            pagination.current_page - 1
                                        )
                                    }
                                    disabled={pagination.current_page === 1}
                                    className="px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {t("pagination.previous")}
                                </button>
                                <span className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {t("pagination.page")}{" "}
                                    {pagination.current_page}{" "}
                                    {t("pagination.of")} {pagination.last_page}
                                </span>
                                <button
                                    onClick={() =>
                                        onPageChange(
                                            pagination.current_page + 1
                                        )
                                    }
                                    disabled={
                                        pagination.current_page ===
                                        pagination.last_page
                                    }
                                    className="px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {t("pagination.next")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
